import React, { useState, useEffect } from 'react';

interface SpacingEffectPreviewProps {
  variant?: 'card' | 'featured';
}

const SpacingEffectPreview: React.FC<SpacingEffectPreviewProps> = ({ variant = 'card' }) => {
  const [activeDay, setActiveDay] = useState(0);
  const [retentionLevels, setRetentionLevels] = useState([100, 100, 100]);

  // Days representing spaced practice
  const days = [
    { label: 'Day 1', hasReview: true },
    { label: 'Day 3', hasReview: true },
    { label: 'Day 7', hasReview: true },
    { label: 'Day 14', hasReview: false },
    { label: 'Day 21', hasReview: true },
    { label: 'Day 30', hasReview: false },
  ];

  // Simulate retention decay and boosts
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveDay(prev => (prev + 1) % days.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Simulate retention levels based on current day
    const baseDecay = [
      [100, 100, 100], // Day 1
      [85, 95, 100],   // Day 3 - spaced shows less decay
      [70, 90, 100],   // Day 7
      [55, 85, 95],    // Day 14 - no review
      [45, 80, 95],    // Day 21 - review boost
      [35, 75, 92],    // Day 30
    ];
    setRetentionLevels(baseDecay[activeDay]);
  }, [activeDay]);

  const getRetentionColor = (level: number) => {
    if (level >= 80) return '#10b981';
    if (level >= 60) return '#3b82f6';
    if (level >= 40) return '#f59e0b';
    return '#ef4444';
  };

  const height = variant === 'featured' ? 'h-48' : 'h-32';

  return (
    <div
      className={`relative ${height} w-full rounded-xl overflow-hidden`}
      style={{
        background: 'linear-gradient(180deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
      }}
    >
      {/* Header */}
      <div
        className="px-3 py-1.5 flex items-center justify-between"
        style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}
      >
        <span className="text-[10px] font-semibold" style={{ color: '#94a3b8' }}>
          Spacing Effect Demo
        </span>
        <div className="flex items-center gap-1">
          <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#10b981' }} />
          <span className="text-[9px]" style={{ color: '#10b981' }}>LIVE</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-3">
        {/* Timeline */}
        <div className="flex items-center justify-between mb-3">
          {days.map((day, idx) => (
            <div key={idx} className="flex flex-col items-center">
              <div
                className={`w-4 h-4 rounded-full flex items-center justify-center transition-all duration-300 ${
                  idx === activeDay ? 'scale-125' : ''
                }`}
                style={{
                  background: idx <= activeDay
                    ? day.hasReview ? '#3b82f6' : 'rgba(59, 130, 246, 0.3)'
                    : 'rgba(255, 255, 255, 0.1)',
                  border: idx === activeDay ? '2px solid #60a5fa' : 'none',
                }}
              >
                {day.hasReview && idx <= activeDay && (
                  <span className="text-[8px] text-white">✓</span>
                )}
              </div>
              <span
                className="text-[8px] mt-1"
                style={{ color: idx === activeDay ? '#e2e8f0' : '#64748b' }}
              >
                {day.label}
              </span>
            </div>
          ))}
        </div>

        {/* Retention comparison bars */}
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <span className="text-[8px] w-14" style={{ color: '#64748b' }}>Cramming</span>
            <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${retentionLevels[0]}%`,
                  background: getRetentionColor(retentionLevels[0]),
                }}
              />
            </div>
            <span className="text-[9px] w-8 text-right font-bold" style={{ color: getRetentionColor(retentionLevels[0]) }}>
              {retentionLevels[0]}%
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[8px] w-14" style={{ color: '#64748b' }}>Daily</span>
            <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${retentionLevels[1]}%`,
                  background: getRetentionColor(retentionLevels[1]),
                }}
              />
            </div>
            <span className="text-[9px] w-8 text-right font-bold" style={{ color: getRetentionColor(retentionLevels[1]) }}>
              {retentionLevels[1]}%
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[8px] w-14 font-semibold" style={{ color: '#3b82f6' }}>Spaced</span>
            <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${retentionLevels[2]}%`,
                  background: getRetentionColor(retentionLevels[2]),
                }}
              />
            </div>
            <span className="text-[9px] w-8 text-right font-bold" style={{ color: getRetentionColor(retentionLevels[2]) }}>
              {retentionLevels[2]}%
            </span>
          </div>
        </div>
      </div>

      {/* Footer hint */}
      <div
        className="absolute bottom-0 left-0 right-0 px-3 py-1 text-center"
        style={{ background: 'rgba(59, 130, 246, 0.1)' }}
      >
        <span className="text-[8px]" style={{ color: '#60a5fa' }}>
          Optimal spacing = 92% retention at Day 30
        </span>
      </div>
    </div>
  );
};

export default SpacingEffectPreview;
